import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import '../styles/Pages.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SERVER = API.replace(/\/api$/, '');

const PhotosPage = () => {
  const { settings } = useApp();
  const currentYear = settings.eventDate ? settings.eventDate.getFullYear() : new Date().getFullYear();

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total }
  const [uploadError, setUploadError] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  useEffect(() => {
    fetch(`${API}/photos`)
      .then(r => r.json())
      .then(data => setPhotos(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setLightbox(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const uploadFiles = useCallback(async (files) => {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    setUploadProgress({ done: 0, total: imageFiles.length });
    setUploadError(false);
    let done = 0;
    let anyError = false;
    for (const file of imageFiles) {
      const form = new FormData();
      form.append('photo', file);
      try {
        const res = await fetch(`${API}/photos`, { method: 'POST', body: form });
        if (res.ok) {
          const saved = await res.json();
          setPhotos(prev => [saved, ...prev]);
        } else {
          anyError = true;
        }
      } catch {
        anyError = true;
      }
      done++;
      setUploadProgress({ done, total: imageFiles.length });
    }
    if (anyError) setUploadError(true);
    setTimeout(() => setUploadProgress(null), 3000);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const onDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current++;
    setDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setDragging(false);
  };
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);
    uploadFiles(e.dataTransfer.files);
  };

  const byYear = photos.reduce((acc, p) => {
    if (!acc[p.year]) acc[p.year] = [];
    acc[p.year].push(p);
    return acc;
  }, {});
  const years = Object.keys(byYear).sort((a, b) => b - a);

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-content"><p style={{ color: '#888' }}>Loading photos…</p></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-content">
        <h2>Event Photos</h2>

        {/* Drop zone */}
        <div className="info-section">
          <h3>📷 {currentYear} CanAm Games Photo Gallery</h3>
          <div
            className={`photo-drop-zone${dragging ? ' dragging' : ''}`}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={e => uploadFiles(e.target.files)}
            />
            {uploadProgress ? (
              <div className="photo-upload-progress">
                <div className="photo-progress-bar-wrap">
                  <div
                    className="photo-progress-bar"
                    style={{ width: `${(uploadProgress.done / uploadProgress.total) * 100}%` }}
                  />
                </div>
                <p>{uploadProgress.done === uploadProgress.total
                  ? (uploadError ? 'Done — some photos failed to upload' : `All ${uploadProgress.total} photo${uploadProgress.total !== 1 ? 's' : ''} uploaded!`)
                  : `Uploading ${uploadProgress.done + 1} of ${uploadProgress.total}…`}
                </p>
              </div>
            ) : (
              <>
                <div className="photo-drop-icon">⬆</div>
                <p className="photo-drop-label">Drop photos here or click to select</p>
                <p className="photo-drop-sub">Select as many as you want — all formats welcome</p>
              </>
            )}
          </div>
        </div>

        {/* Gallery */}
        {photos.length === 0 ? (
          <div className="info-section">
            <p className="event-description">No photos yet — be the first to drop some in!</p>
          </div>
        ) : (
          years.map(year => (
            <div key={year} className="info-section">
              <h3>📸 {year} CanAm</h3>
              <div className="photo-grid">
                {byYear[year].map(p => (
                  <div key={p.id} className="photo-thumb-wrap" onClick={() => setLightbox(p)}>
                    <img
                      src={`${SERVER}${p.photoUrl}`}
                      alt={`${year} CanAm`}
                      className="photo-thumb"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
            <img
              src={`${SERVER}${lightbox.photoUrl}`}
              alt="CanAm"
              className="lightbox-img"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotosPage;
